// platform.identity — user keypair lifecycle.
//
// On first sign-in the SDK mints a per-user ECDH P-256 keypair, persists
// it locally (IndexedDB in browsers; Map-backed for tests/SSR), and
// publishes the public half to `s3db/shared/users/{sub}/pubkey` in the
// project's bucket so other users can encrypt to them (member-readable;
// the member floor's WriteOwnIdentity grants each user their own subtree).
//
// Trust model (POC): TOFU on pubkey-publish. A user is whoever owns the
// keypair stored at their sub locally — the published pubkey isn't signed
// by an external authority. First-fetched copy wins for other users.
// Documented in CRYPTO.md / E2E_DESIGN.md as an honest edge.

import {
  GetObjectCommand,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import {
  type RecipientPrivateKey,
  exportPublicKeyRaw,
  importPublicKeyRaw,
} from "@s3p/s3db";
import { paths } from "./paths.ts";

// ─────────────────────────────────────────────────────────────────────
// Storage interface — pluggable so tests use a Map; prod uses IndexedDB
// ─────────────────────────────────────────────────────────────────────

/** Persisted form of a user's keypair. JWK + raw both stored: JWK is
 *  the portable format we re-import from; raw is what we publish to
 *  S3 so other clients can fetch a self-describing key. */
export interface StoredKeypair {
  sub: string;
  publicKeyJwk: JsonWebKey;
  privateKeyJwk: JsonWebKey;
  createdAt: string; // ISO 8601
}

export interface IdentityStorage {
  get(sub: string): Promise<StoredKeypair | null>;
  put(rec: StoredKeypair): Promise<void>;
  /** Optional — clears all stored keypairs. Used by logout flows / tests. */
  clear?(): Promise<void>;
}

/** In-memory implementation. Default for tests and SSR; lost on reload. */
export function createMemoryStorage(): IdentityStorage {
  const m = new Map<string, StoredKeypair>();
  return {
    async get(sub) {
      return m.get(sub) ?? null;
    },
    async put(rec) {
      m.set(rec.sub, rec);
    },
    async clear() {
      m.clear();
    },
  };
}

/** IndexedDB-backed implementation for browsers. Survives page reloads
 *  but is bound to the same origin (which is exactly what we want —
 *  one user's key per device per realm). */
export function createIndexedDBStorage(dbName = "s3p-identity"): IdentityStorage {
  const STORE = "keypairs";

  function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(dbName, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(STORE, { keyPath: "sub" });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  function txOp<T>(mode: IDBTransactionMode, op: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      openDB().then((db) => {
        const tx = db.transaction(STORE, mode);
        const req = op(tx.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }, reject);
    });
  }

  return {
    async get(sub) {
      const rec = (await txOp("readonly", (s) => s.get(sub))) as StoredKeypair | undefined;
      return rec ?? null;
    },
    async put(rec) {
      await txOp("readwrite", (s) => s.put(rec));
    },
    async clear() {
      await txOp("readwrite", (s) => s.clear());
    },
  };
}

// ─────────────────────────────────────────────────────────────────────
// Identity module
// ─────────────────────────────────────────────────────────────────────

export interface IdentityConfig {
  /** Where to obtain a (possibly-refreshed) S3 client from. Same shape
   *  the documents/rollups modules use. */
  getClient: () => Promise<S3Client>;
  /** Bucket the project owns (where pubkeys are published). */
  bucket: string;
  /** Local keypair storage. Defaults to IndexedDB in browsers, Map otherwise. */
  storage?: IdentityStorage;
  /** Optional override of the pubkey publish path.
   *  Default: paths().userPubkey(sub) — `s3db/shared/users/{sub}/pubkey`. */
  pubkeyPath?: (sub: string) => string;
}

export interface IdentityModule {
  /** Get-or-create the user's keypair. Idempotent: returns the same
   *  keys on every call within and across sessions (persisted in
   *  storage). */
  ensureKeys(sub: string): Promise<{
    publicKey: CryptoKey;
    privateKey: CryptoKey;
  }>;
  /** Publish the user's pubkey to S3. Idempotent — overwrites if present
   *  with the same bytes (or different, if rotated locally). */
  publishPublicKey(sub: string): Promise<{ key: string; sizeBytes: number }>;
  /** Fetch ANOTHER user's pubkey from S3 (or this user's, equivalently).
   *  No signature verification yet — TOFU per POC. */
  fetchPublicKey(sub: string): Promise<CryptoKey>;
  /** Convenience: the user's RecipientPrivateKey, shaped for handing
   *  straight to the s3db crypto adapter's resolver. */
  getMyPrivateKey(sub: string): Promise<RecipientPrivateKey>;
}

function defaultStorage(): IdentityStorage {
  // In a browser tab where IndexedDB is available, use it. Otherwise
  // (vitest with happy-dom that lacks IDB, SSR, Node scripts) fall
  // back to the Map-backed implementation so the SDK still loads.
  try {
    if (typeof indexedDB !== "undefined" && indexedDB) {
      return createIndexedDBStorage();
    }
  } catch {
    // happy-dom can throw on access; treat as unavailable.
  }
  return createMemoryStorage();
}

async function generateKeypairJwks(): Promise<{
  publicKey: CryptoKey;
  privateKey: CryptoKey;
  publicKeyJwk: JsonWebKey;
  privateKeyJwk: JsonWebKey;
}> {
  // ECDH P-256, extractable=true so we can persist via JWK.
  // (Non-extractable would be tighter but then we can't survive reload.)
  const kp = (await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, [
    "deriveBits",
    "deriveKey",
  ])) as CryptoKeyPair;
  const publicKeyJwk = await crypto.subtle.exportKey("jwk", kp.publicKey);
  const privateKeyJwk = await crypto.subtle.exportKey("jwk", kp.privateKey);
  return { publicKey: kp.publicKey, privateKey: kp.privateKey, publicKeyJwk, privateKeyJwk };
}

async function importKeypairFromJwks(
  publicKeyJwk: JsonWebKey,
  privateKeyJwk: JsonWebKey,
): Promise<{ publicKey: CryptoKey; privateKey: CryptoKey }> {
  const publicKey = await crypto.subtle.importKey(
    "jwk",
    publicKeyJwk,
    { name: "ECDH", namedCurve: "P-256" },
    true,
    [],
  );
  const privateKey = await crypto.subtle.importKey(
    "jwk",
    privateKeyJwk,
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveBits", "deriveKey"],
  );
  return { publicKey, privateKey };
}

export function createIdentity(config: IdentityConfig): IdentityModule {
  const storage = config.storage ?? defaultStorage();
  const pubkeyPath = config.pubkeyPath ?? ((sub: string) => paths().userPubkey(sub));

  // Per-session cache so ensureKeys() is cheap after the first call.
  const cache = new Map<string, { publicKey: CryptoKey; privateKey: CryptoKey }>();

  async function ensureKeys(sub: string) {
    const cached = cache.get(sub);
    if (cached) return cached;
    const stored = await storage.get(sub);
    if (stored) {
      const imported = await importKeypairFromJwks(stored.publicKeyJwk, stored.privateKeyJwk);
      cache.set(sub, imported);
      return imported;
    }
    const fresh = await generateKeypairJwks();
    await storage.put({
      sub,
      publicKeyJwk: fresh.publicKeyJwk,
      privateKeyJwk: fresh.privateKeyJwk,
      createdAt: new Date().toISOString(),
    });
    const out = { publicKey: fresh.publicKey, privateKey: fresh.privateKey };
    cache.set(sub, out);
    return out;
  }

  async function publishPublicKey(sub: string) {
    const { publicKey } = await ensureKeys(sub);
    const raw = await exportPublicKeyRaw(publicKey);
    const key = pubkeyPath(sub);
    const s3 = await config.getClient();
    await s3.send(
      new PutObjectCommand({
        Bucket: config.bucket,
        Key: key,
        Body: raw,
        ContentType: "application/octet-stream",
      }),
    );
    return { key, sizeBytes: raw.byteLength };
  }

  async function fetchPublicKey(sub: string): Promise<CryptoKey> {
    const key = pubkeyPath(sub);
    const s3 = await config.getClient();
    const res = await s3.send(new GetObjectCommand({ Bucket: config.bucket, Key: key }));
    const bytes = await (
      res.Body as { transformToByteArray: () => Promise<Uint8Array> }
    ).transformToByteArray();
    return importPublicKeyRaw(bytes);
  }

  async function getMyPrivateKey(sub: string): Promise<RecipientPrivateKey> {
    const { privateKey } = await ensureKeys(sub);
    return { kind: "user", id: sub, privateKey };
  }

  return { ensureKeys, publishPublicKey, fetchPublicKey, getMyPrivateKey };
}
